import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Tippy from '@tippyjs/react';
import cs from 'classnames';
import { useRecoilValue } from 'recoil';

import { deleteCustomReport, updateFavoriteReport } from 'api/Reports';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import useModal from 'lib/hooks/useModal';

import Icon from 'components/shared/Icon/Icon';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Typography from 'components/shared/Typography/Typography';
import state from 'components/state';

import { MESSAGES } from './messages';

const ReportItem = ({ report }) => {
  const navigate = useNavigate();

  const [_, setSearchParams] = useSearchParams();

  const user = useRecoilValue(state.userState);
  const permissions = useRecoilValue(state.permissions);

  const { isOpen, openModal, closeModal } = useModal();

  const isCustomReport = location.pathname.endsWith('/custom');
  const isFavorite = !!report?.is_favorite;
  const isCurrentUser =
    (report?.created_by_type === 0 && permissions?.admin_id === report?.created_by) ||
    (report?.created_by_type === 1 && user?.id === report?.created_by);
  const category = isCustomReport ? report?.report?.category : report?.category;

  const { mutateAsync: updateFavorite } = useCustomMutation({
    mutationFn: (params) => updateFavoriteReport(navigate, params),
    errorMessage: MESSAGES.favorite[isFavorite].error,
    successMessage: MESSAGES.favorite[isFavorite].success,
    refetchQueryKey: [
      'favorite-reports',
      'custom-reports',
      category ? ['reports', category] : 'reports'
    ]
  });

  const { mutateAsync: deleteReportHandler } = useCustomMutation({
    mutationFn: (params) => deleteCustomReport(navigate, params),
    errorMessage: MESSAGES.delete.error,
    successMessage: MESSAGES.delete.success,
    refetchQueryKey: ['favorite-reports', 'custom-reports']
  });

  const onFavoriteChangeHandler = async () => {
    await updateFavorite({
      id: report.id,
      isFavorite: !isFavorite,
      isCustomReport: isCustomReport || !!report?.reportCustom // reportCustom is from favorites
    });
  };

  const onReportClick = (navigateProps) => {
    setSearchParams((prev) => {
      if (isCustomReport) {
        prev.set('category', category);
      }

      prev.set('key', report.key);
      prev.set('reportId', report.id);
      return prev;
    }, navigateProps);
  };

  const options = [
    {
      label: isCurrentUser ? 'Edit' : 'Customize Report',
      icon: 'file-download-mono',
      onClick: () => onReportClick({ state: { customize: true } })
    },
    ...(isCustomReport && isCurrentUser
      ? [{ label: 'Delete', onClick: openModal, icon: 'trash-mono' }]
      : [])
  ];

  const deleteReport = async () => {
    await deleteReportHandler({ id: report.id });
    closeModal();
  };

  return (
    <div
      className={cs(
        'group flex items-center justify-between gap-4',
        'border-0 border-b border-solid border-neutral-200',
        'transition-all duration-300 hover:border-primary-500'
      )}>
      <div onClick={onReportClick} className="flex flex-1 cursor-pointer items-center gap-2 p-2">
        <Typography className="cursor-pointer transition-all duration-300 group-hover:text-primary-500">
          {report.name}
        </Typography>
        {report.description && (
          <Tippy className="tippy-dark" content={report.description}>
            <div>
              <Icon icon="info-filled" className="cursor-pointer" color="neutral" size={16} />
            </div>
          </Tippy>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Icon
          icon={isFavorite ? 'star-filled' : 'star-linear'}
          onClick={onFavoriteChangeHandler}
          color={isFavorite ? 'warning' : 'neutral'}
        />
        <Popover
          isFixed
          options={options}
          position="left"
          icon="context-menu-vertical"
          isBorder={false}
        />
      </div>

      <Confirm
        isOpen={isOpen}
        handleClose={closeModal}
        handleContinue={deleteReport}
        title="Delete Report"
        message="Are you sure you want to delete this report? This action cannot be undone."
        icon="trash-mono"
        iconColor="danger"
        variant="danger"
      />
    </div>
  );
};

export default ReportItem;
